// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { formatAmount } from '../../lib/eventLog.js';
import { BottomSheet } from './BottomSheet.js';
import { SearchIcon } from './icons.js';
import type { Account } from '../../types.js';

interface AccountPickerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSelect: (accountId: string) => void;
  // Optional filter (e.g. only expense accounts for the "To" field).
  filter?: (account: Account) => boolean;
}

// DECISION (Phase 7): replaces the desktop's long native <select> of accounts
// with a searchable full-path list in a bottom sheet — the native-feeling way to
// pick from a deep chart of accounts on a phone. Shows each account's full
// colon-path so ambiguous leaf names (two "Interest" accounts) stay distinct.
export function AccountPicker({ open, title, onClose, onSelect, filter }: AccountPickerProps) {
  const { t } = useTranslation(['mobile']);
  const { state } = useApp();
  const { ledger } = state;
  const [query, setQuery] = useState('');

  const accounts = useMemo(() => {
    const all = [...ledger.accounts.values()].filter(
      (a) => a.type !== 'ROOT' && !a.placeholder && !a.hidden,
    );
    const named = all.map((a) => ({ account: a, path: fullPath(a, ledger.accounts) }));
    named.sort((x, y) => x.path.localeCompare(y.path));
    const filtered = filter ? named.filter((n) => filter(n.account)) : named;
    if (!query.trim()) return filtered;
    const q = query.toLowerCase();
    return filtered.filter((n) => n.path.toLowerCase().includes(q));
  }, [ledger.accounts, filter, query]);

  return (
    <BottomSheet open={open} onClose={onClose} title={title} tall>
      <div className="sticky top-0 bg-bg-surface px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2 bg-bg border border-border px-3 h-11">
          <SearchIcon size={16} className="text-text-muted shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('accountPicker.searchAccountsPlaceholder')}
            className="flex-1 bg-transparent text-text text-md focus:outline-none placeholder-text-subtle"
          />
        </div>
      </div>
      <ul>
        {accounts.map(({ account, path }) => (
          <li key={account.id}>
            <button
              onClick={() => {
                onSelect(account.id);
                onClose();
                setQuery('');
              }}
              className="w-full text-left px-4 touch-row flex items-center justify-between gap-3 border-b border-border active:bg-bg-elevated"
            >
              <span className="text-md text-text truncate">{path}</span>
              <span className={`text-sm font-mono shrink-0 ${account.balance < 0 ? 'text-negative' : 'text-text-muted'}`}>
                {formatAmount(account.balance, account.currency)}
              </span>
            </button>
          </li>
        ))}
        {accounts.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-text-muted">{t('accountPicker.noMatching')}</li>
        )}
      </ul>
    </BottomSheet>
  );
}

// Build the full "Parent:Child:Leaf" path for an account (skipping ROOT).
function fullPath(account: Account, accounts: Map<string, Account>): string {
  const parts: string[] = [account.name];
  let current = account.parentId ? accounts.get(account.parentId) : undefined;
  while (current && current.type !== 'ROOT') {
    parts.unshift(current.name);
    current = current.parentId ? accounts.get(current.parentId) : undefined;
  }
  return parts.join(':');
}
